import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Eye, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyProjects = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects`, {
        withCredentials: true
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Active': 'bg-lime-100 text-lime-800 border-lime-200',
      'Completed': 'bg-stone-100 text-stone-800 border-stone-200',
      'On Hold': 'bg-amber-100 text-amber-800 border-amber-200'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${styles[status] || styles['Active']}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="text-stone-600">Loading your projects...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          My Projects
        </h1>
        <p className="mt-2 text-base text-stone-600">Track the progress of your interior projects</p>
      </div>

      {projects.length === 0 ? (
        <div className="bg-white rounded-lg border border-stone-200 p-12 text-center">
          <Clock className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h3 className="text-lg font-medium text-stone-900 mb-2">No Projects Yet</h3>
          <p className="text-stone-600">Your projects will appear here once they are created.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <div
              key={project.project_id}
              data-testid={`project-card-${project.project_id}`}
              className="bg-white rounded-lg border border-stone-200 p-6 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {project.project_name}
                </h3>
                {getStatusBadge(project.status)}
              </div>

              <div className="space-y-2 text-sm mb-4">
                {project.site_address && (
                  <div>
                    <span className="text-stone-500">Site:</span>
                    <span className="ml-2 text-stone-900">{project.site_address}</span>
                  </div>
                )}
                {project.start_date && (
                  <div>
                    <span className="text-stone-500">Start Date:</span>
                    <span className="ml-2 text-stone-900 tabular-nums">{project.start_date}</span>
                  </div>
                )}
                {project.expected_end_date && (
                  <div>
                    <span className="text-stone-500">Expected End:</span>
                    <span className="ml-2 text-stone-900 tabular-nums">{project.expected_end_date}</span>
                  </div>
                )}
                {project.notes && (
                  <div>
                    <span className="text-stone-500">Notes:</span>
                    <p className="mt-1 text-stone-700">{project.notes}</p>
                  </div>
                )}
              </div>

              <Button
                data-testid={`view-orders-${project.project_id}`}
                onClick={() => navigate(`/my-orders?project_id=${project.project_id}`)}
                className="w-full bg-amber-700 hover:bg-amber-800 text-white"
                size="sm"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Orders
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyProjects;
